import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";

const BANDS = ["V1", "V2", "V3", "V4", "V5", "V6", "V7"];
const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(1));

/** A3: frozen snapshot info, or warning + admin "Congela snapshot" for legacy certificates. */
export default function SnapshotBanner({ results, assessmentId, isAdmin, onFrozen }) {
  const [open, setOpen] = useState(false);
  if (!results || results.snapshot_status === "not_issued") return null;
  if (results.snapshot_status === "frozen") {
    const s = results.snapshot;
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="snapshot-frozen-banner">
        <Lock className="w-4 h-4" />
        Valori congelati nello snapshot del certificato ({s.methodology_revision}) · {s.source === "emissione" ? "creato all'emissione" : `congelato da ${s.created_by_name}`}. Le modifiche a pesi e benchmark non li alterano.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex flex-wrap items-center gap-3" data-testid="snapshot-missing-banner">
      <AlertTriangle className="w-4 h-4" />
      <span className="flex-1">Snapshot non ancora congelato: i valori mostrati sono ricalcolati con pesi e benchmark attuali.</span>
      {isAdmin && (
        <Button size="sm" onClick={() => setOpen(true)} data-testid="snapshot-freeze-open" className="bg-amber-600 hover:bg-amber-700 text-white">Congela snapshot</Button>
      )}
      {open && <FreezeDialog open={open} onClose={() => setOpen(false)} results={results} assessmentId={assessmentId} onFrozen={onFrozen} />}
    </div>
  );
}

function FreezeDialog({ open, onClose, results, assessmentId, onFrozen }) {
  const [f, setF] = useState({ final_rating: "", band: "", score_e: "", score_s: "", score_g: "" });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const ready = confirm && f.band && ["final_rating", "score_e", "score_s", "score_g"].every((k) => f[k] !== "");

  async function submit() {
    setBusy(true);
    try {
      const body = { band: f.band, confirm: true, final_rating: Number(f.final_rating), score_e: Number(f.score_e), score_s: Number(f.score_s), score_g: Number(f.score_g) };
      await api.post(`/assessments/${assessmentId}/rating-snapshot`, body);
      toast.success("Snapshot congelato: i valori del certificato sono ora immutabili");
      onClose(); onFrozen?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  }

  const num = (k, label) => (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input type="number" step="0.1" min={0} max={100} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-9 mt-1" data-testid={`snapshot-${k}`} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="snapshot-freeze-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Congela snapshot del certificato</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Valori calcolati oggi (solo riferimento): rating {fmt(results.final_rating)} · banda {results.band || "—"} · E {fmt(results.score_e)} · S {fmt(results.score_s)} · G {fmt(results.score_g)}. Topic e indicatori vengono congelati dal calcolo attuale.</p>
        <div className="grid grid-cols-2 gap-3">
          {num("final_rating", "Rating finale *")}
          <div>
            <Label className="text-xs font-semibold text-slate-600">Banda *</Label>
            <Select value={f.band} onValueChange={(v) => setF({ ...f, band: v })}>
              <SelectTrigger className="h-9 mt-1" data-testid="snapshot-band"><SelectValue placeholder="V1–V7" /></SelectTrigger>
              <SelectContent>{BANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {num("score_e", "Score E *")}{num("score_s", "Score S *")}{num("score_g", "Score G *")}
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-800 mt-2">
          <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} data-testid="snapshot-confirm" className="mt-0.5" />
          Confermo i valori: lo snapshot è immutabile e non potrà essere modificato.
        </label>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button disabled={!ready || busy} onClick={submit} data-testid="snapshot-freeze-submit" className="bg-viva-500 hover:bg-viva-600 text-white">Congela</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
